import type { FC } from 'react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Close from '@mui/icons-material/Close';
import DownloadRounded from '@mui/icons-material/DownloadRounded';
import Verified from '@mui/icons-material/Verified';
import {
  Box,
  CircularProgress,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';

import { DownloadState } from 'src/constants/downloadManager';
import type { ExportDownload } from 'src/types/downloadManager';
import { useDownloadManager } from 'src/components/downloadManager/DownloadManagerContext';

import { styles } from './index.styles';

interface DownloadManagerSectionProps {
  title: string;
  downloads: Record<string, ExportDownload[]>;
  state: DownloadState;
}

const DownloadManagerSection: FC<DownloadManagerSectionProps> = ({
  downloads,
  state,
}) => {
  const { t } = useTranslation('downloadManager');
  const { downloadFile, hideExport } = useDownloadManager();
  const theme = useTheme();
  const [message, Icon] = useMemo(() => {
    switch (String(state)) {
      case DownloadState.PENDING:
        return [
          t('downloadManager:DownloadState.Pending'),
          <CircularProgress size={24} color="primary" key={1} />,
        ];
      case DownloadState.READY:
        return [
          t('downloadManager:DownloadState.Ready'),
          <DownloadRounded color="primary" key={1} />,
        ];
      case DownloadState.DOWNLOADING:
        return [
          t('downloadManager:DownloadState.Downloading'),
          <CircularProgress size={24} color="primary" key={1} />,
        ];
      case DownloadState.FINISHED:
        return [
          t('downloadManager:DownloadState.Finished'),
          <Verified color="success" key={1} />,
        ];
      case DownloadState.ERROR:
        return [t('downloadManager:DownloadState.Error')];
      default:
        return [t('downloadManager:DownloadState.Pending')];
    }
  }, [state, t]);

  if (Object.keys(downloads).length === 0) {
    return null;
  }
  return (
    <Box sx={styles.root}>
      <Typography
        variant="h6"
        sx={{
          mb: 1.5,
        }}
      >
        {message}
      </Typography>
      {Object.keys(downloads).map((category) => (
        <Box key={category}>
          <Typography variant="h6" sx={styles.category}>
            {t(`downloadManager:Category.${category}`)}
          </Typography>
          {downloads[category].map((download) => (
            <Box sx={styles.element(theme, state)} key={download.id}>
              <IconButton
                disabled={download.status !== DownloadState.READY}
                onClick={() => downloadFile(download)}
                data-testid="download-icon-button"
              >
                {Icon}
              </IconButton>
              <Typography sx={styles.name} title={download.name}>
                {download.name}
              </Typography>
              <IconButton
                size="small"
                sx={styles.cancelButton}
                onClick={() => hideExport(download.id)}
              >
                <Close color="primary" />
              </IconButton>
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
};

export default DownloadManagerSection;
